import React, { useState, useMemo, useEffect, useCallback } from 'react'
import {
  Box, Typography, Stack, Chip, IconButton, Button, Divider,
  Menu, MenuItem, CircularProgress,
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import RefreshIcon from '@mui/icons-material/Refresh'
import StarIcon from '@mui/icons-material/Star'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import SlidePanel from '../../components/common/SlidePanel'
import EmptyState from '../../components/common/EmptyState'
import ToneSegmentCard from './ToneSegmentCard'
import { FS, SP, PANEL_SX, RAIL_WIDTH } from '../../theme/tokens'

const API = process.env.REACT_APP_API_BASE_URL

// ── Reject reason tags ──────────────────────────────────────────────────────

const REJECT_REASONS = [
  { tag: 'roles_wrong',       label: 'Roles wrong' },
  { tag: 'signature_left_in', label: 'Signature left in' },
  { tag: 'messages_merged',   label: 'Messages merged' },
  { tag: 'messages_missing',  label: 'Messages missing' },
  { tag: 'wrong_language',    label: 'Wrong language' },
  { tag: 'wrong_topic',       label: 'Wrong topic' },
  { tag: 'other',             label: 'Other' },
]

// ── Richness dot helper ─────────────────────────────────────────────────────

const RICHNESS_MAP = { high: 3, medium: 2, low: 1 }

function RichnessDots({ level }) {
  const filled = RICHNESS_MAP[level] ?? 0
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: i < filled ? 'warning.main' : 'action.disabled',
          }}
        />
      ))}
    </Stack>
  )
}

// ── ToneReviewPanel ─────────────────────────────────────────────────────────

export default function ToneReviewPanel({
  open,
  onClose,
  example,
  onApproved,
  onRejected,
}) {
  // ── Local state ───────────────────────────────────────────────────────────
  const [segments, setSegments]     = useState([])
  const [comments, setComments]     = useState({})
  const [saving, setSaving]         = useState(false)
  const [analyzing, setAnalyzing]   = useState(false)
  const [rejectAnchor, setRejectAnchor] = useState(null)

  // Reset when example changes
  useEffect(() => {
    if (!example) return
    setSegments(example.full_thread ?? [])
    setComments(example.segment_comments ?? {})
  }, [example?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parsed summary ────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!example?.llm_thread_summary) return null
    try { return JSON.parse(example.llm_thread_summary) }
    catch { return null }
  }, [example?.llm_thread_summary])

  const flags = useMemo(() => example?.llm_parsing_flags ?? [], [example?.llm_parsing_flags])

  const flagMap = useMemo(() => {
    const m = {}
    flags.forEach((f) => { m[f.segment_index] = f })
    return m
  }, [flags])

  // ── Drift detection ───────────────────────────────────────────────────────
  const hasChanges = example
    ? JSON.stringify(segments) !== JSON.stringify(example.full_thread)
    : false

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSwapRole = useCallback((idx) => {
    setSegments((prev) => {
      const next = [...prev]
      const seg = { ...next[idx] }
      seg.role = seg.role === 'samuel' ? 'customer' : 'samuel'
      next[idx] = seg
      return next
    })
  }, [])

  const handleDeleteSegment = useCallback((idx) => {
    setSegments((prev) => prev.filter((_, i) => i !== idx))
  }, [])

  const handleComment = useCallback(async (idx, text) => {
    setComments((prev) => ({ ...prev, [String(idx)]: text }))
    if (!example?.id) return
    try {
      const res = await fetch(`${API}/api/inquiry/tone-examples/${example.id}/segment-comment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ segment_index: idx, comment: text }),
      })
      if (res.ok) {
        const data = await res.json()
        setComments(data.segment_comments ?? {})
      }
    } catch { /* silent */ }
  }, [example?.id])

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return
    const src = result.source.index
    const dst = result.destination.index
    if (src === dst) return
    setSegments((prev) => {
      const next = [...prev]
      const [moved] = next.splice(src, 1)
      next.splice(dst, 0, moved)
      return next
    })
  }, [])

  const handleAnalyze = useCallback(async () => {
    if (!example?.id) return
    setAnalyzing(true)
    try {
      await fetch(`${API}/api/inquiry/tone-examples/${example.id}/analyze`, {
        method: 'POST',
      })
    } catch { /* silent */ }
    setAnalyzing(false)
  }, [example?.id])

  const saveReorder = useCallback(async () => {
    if (!example?.id) return
    await fetch(`${API}/api/inquiry/tone-examples/${example.id}/reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ full_thread: segments }),
    })
  }, [example?.id, segments])

  const handleApprove = useCallback(async (featured = false) => {
    if (!example?.id) return
    setSaving(true)
    try {
      if (hasChanges) await saveReorder()
      await fetch(`${API}/api/inquiry/tone-examples/${example.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: true, is_featured: featured }),
      })
      onApproved?.()
    } catch { /* silent */ }
    setSaving(false)
  }, [example?.id, hasChanges, saveReorder, onApproved])

  const handleReject = useCallback(async (reasonTag) => {
    if (!example?.id) return
    setRejectAnchor(null)
    setSaving(true)
    try {
      await fetch(`${API}/api/inquiry/tone-examples/${example.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason_tag: reasonTag }),
      })
      onRejected?.()
    } catch { /* silent */ }
    setSaving(false)
  }, [example?.id, onRejected])

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <SlidePanel open={open} onClose={onClose} title="Tone Review" width={640}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* ── Section 1: Intelligence Card ────────────────────────────── */}
        <Box
          sx={{
            ...PANEL_SX,
            borderLeft: `${RAIL_WIDTH}px solid`,
            borderLeftColor: 'info.main',
            mx: SP.sm,
            mt: SP.sm,
            flexShrink: 0,
          }}
        >
          {summary ? (
            <>
              <Typography variant="body2" sx={{ fontSize: FS.base, mb: SP.tight }}>
                {summary.summary ?? summary.text ?? ''}
              </Typography>

              <Stack direction="row" spacing={SP.tight} alignItems="center" flexWrap="wrap">
                {summary.conversation_arc && (
                  <Chip label={summary.conversation_arc} size="small" color="info" />
                )}
                {summary.customer_sentiment_shift && (
                  <Chip label={summary.customer_sentiment_shift} size="small" color="info" />
                )}
                {summary.tone_richness && (
                  <RichnessDots level={summary.tone_richness} />
                )}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={SP.tight} sx={{ mt: SP.tight }}>
                {flags.length > 0 && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label={`${flags.length} flag${flags.length > 1 ? 's' : ''}`}
                    size="small"
                    color="warning"
                  />
                )}
                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  title="Re-analyze"
                >
                  {analyzing
                    ? <CircularProgress size={16} />
                    : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Stack>
            </>
          ) : (
            // `summary` fehlt, weil das Beispiel noch keine Analyse traegt —
            // kein Abruf, kein Fehlerzweig in dieser Datei, also no-data.
            <EmptyState
              reason="no-data"
              message="No analysis yet"
              action={
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  startIcon={analyzing ? <CircularProgress size={14} /> : undefined}
                >
                  Analyze
                </Button>
              }
            />
          )}
        </Box>

        {/* ── Section 2: Conversation Timeline ────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: SP.sm, mt: SP.tight }}>
          <Stack direction="row" alignItems="center" sx={{ mb: SP.tight }}>
            <Typography variant="subtitle2" sx={{ fontSize: FS.md }}>
              Conversation ({segments.length} messages)
            </Typography>
            <Box sx={{ flex: 1 }} />
            {hasChanges && (
              <Button
                size="small"
                variant="outlined"
                disabled={saving}
                onClick={saveReorder}
              >
                Save order
              </Button>
            )}
          </Stack>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="tone-segments">
              {(provided) => (
                <Stack
                  spacing={SP.tight}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {segments.map((seg, i) => (
                    <Draggable key={`seg-${i}`} draggableId={`seg-${i}`} index={i}>
                      {(prov) => (
                        <Box
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                        >
                          <ToneSegmentCard
                            segment={seg}
                            index={i}
                            flag={flagMap[i]}
                            comment={comments[String(i)]}
                            dragHandleProps={prov.dragHandleProps}
                            onSwapRole={handleSwapRole}
                            onDelete={handleDeleteSegment}
                            onComment={handleComment}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        </Box>

        {/* ── Section 3: Fixed Footer ─────────────────────────────────── */}
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          spacing={SP.tight}
          sx={{ px: SP.sm, py: SP.tight, flexShrink: 0 }}
        >
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={saving}
            onClick={(e) => setRejectAnchor(e.currentTarget)}
          >
            Reject
          </Button>
          <Menu
            anchorEl={rejectAnchor}
            open={Boolean(rejectAnchor)}
            onClose={() => setRejectAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {REJECT_REASONS.map((r) => (
              <MenuItem key={r.tag} onClick={() => handleReject(r.tag)}>
                {r.label}
              </MenuItem>
            ))}
          </Menu>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            size="small"
            disabled={saving}
            startIcon={<StarIcon />}
            onClick={() => handleApprove(true)}
          >
            Featured
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={saving}
            onClick={() => handleApprove(false)}
          >
            Approve
          </Button>
        </Stack>
      </Box>
    </SlidePanel>
  )
}
